import { Form, Formik } from 'formik';

import { Button } from '../../cdl/Button/Button';
import { FormikCheckbox } from '../../cdl/Checkbox/FormikCheckbox';
import { Modal } from '../../cdl/Modal/Modal';
import { Text } from '../../cdl/Text/Text';
import { FormikTextArea } from '../../cdl/TextArea/FormikTextArea';
import { translate } from '../helpers/translate.helper';
import { Box } from '../ui/Box';

export interface CancelPopupText {
    headline: string;
    description: string;
    buttonText: string;
    acknowledgement?: string;
}

interface CancelPopupValues {
    cancelReason: string;
    acknowledgement: boolean;
}

interface CancelPopupProps {
    isOpen: boolean;
    onSubmit: (payload: { cancelReason: string }) => void;
    onDismiss: () => void;
    isSubmitting: boolean;
    text: CancelPopupText;
    requireReason?: boolean;
}

export const CancelPopup = ({
    isOpen,
    onSubmit,
    onDismiss,
    isSubmitting,
    text,
    requireReason = true,
}: CancelPopupProps) => {
    const validate = (values: CancelPopupValues) => {
        const errors: Partial<Record<keyof CancelPopupValues, string>> = {};

        if (requireReason && !values.cancelReason.trim()) {
            errors.cancelReason = translate('alert.cancelorder.error');
        }

        return errors;
    };

    return (
        <Modal isOpen={isOpen} size="medium" scrollable={false} onDismiss={onDismiss} dismissible={false}>
            <Formik
                validateOnBlur={false}
                validate={validate}
                initialValues={{ cancelReason: '', acknowledgement: false }}
                onSubmit={({ cancelReason }) => onSubmit({ cancelReason })}
            >
                {({ values }) => (
                    <Form>
                        <Box display="grid" gap={16} padding={24}>
                            <Text variant="title">{text.headline}</Text>

                            <Text variant="small" color="foreground.muted">
                                {text.description}
                            </Text>

                            <Box display="flex" flexDirection="column" gap={24}>
                                <FormikTextArea name="cancelReason" label={translate('alert.cancelorder.label')} />

                                {text.acknowledgement ? (
                                    <FormikCheckbox name="acknowledgement" label={text.acknowledgement} />
                                ) : null}

                                <Box display="flex" justifyContent="end" gap={8}>
                                    <Button emphasis="medium" type="button" onClick={onDismiss}>
                                        {translate('btn.goBack')}
                                    </Button>
                                    <Button
                                        emphasis="high"
                                        type="submit"
                                        destructive
                                        isLoading={isSubmitting}
                                        disabled={
                                            (requireReason && !values.cancelReason.trim()) ||
                                            (!!text.acknowledgement && !values.acknowledgement)
                                        }
                                    >
                                        {text.buttonText}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};
